// src/context/SessionContext.js
import React, { createContext, useContext, useState, useCallback } from "react";
import axiosInstance from "../AxiosInstance";
import config from "../config/config";
import { useSessionSocket } from "../websockets/SessionSocketService";

const SessionContext = createContext();

export function SessionProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * LIST SESSIONS FOR A GIVEN AGREEMENT
   * GET /v1/sessions/agreement/{agreementId}
   */
  const listSessionsByAgreement = useCallback(
    async (agreementId) => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(
          `${config.API_BASE_URL}/v1/sessions/agreement/${agreementId}`
        );
        return data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * WORKER REQUESTS SESSION START
   * PATCH /v1/sessions/{sessionId}/request-start
   */
  const requestSessionStart = useCallback(
    async (agreementId) => {
      setLoading(true);
      try {
        const sessions = await listSessionsByAgreement(agreementId);
        const next = sessions.find((s) => s.status === "PENDING");
        if (!next) throw new Error("No pending session to start");

        const { data } = await axiosInstance.patch(
          `${config.API_BASE_URL}/v1/sessions/${next.sessionId}/request-start`
        );
        return data;
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [listSessionsByAgreement]
  );

  /**
   * CLIENT APPROVES SESSION START
   * PATCH /v1/sessions/{sessionId}/approve-start
   */
  const approveSessionStart = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const resp = await axiosInstance.patch(
        `${config.API_BASE_URL}/v1/sessions/${sessionId}/approve-start`
      );
      setSessions((s) => s.map(sess => sess.sessionId === sessionId ? resp.data : sess));
      return resp.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * WORKER / CLIENT MARKS SESSION COMPLETE
   * PATCH /v1/sessions/{sessionId}/complete
   */
  const completeSession = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const resp = await axiosInstance.patch(
        `${config.API_BASE_URL}/v1/sessions/${sessionId}/complete`
      );
      setSessions((s) => s.map(sess => sess.sessionId === sessionId ? resp.data : sess));
      return resp.data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUpdate = useCallback((updated) => {
    setSessions(prev =>
      prev.map(s => (s.sessionId === updated.sessionId ? updated : s))
    );
  }, []);

  useSessionSocket(handleUpdate);

  return (
    <SessionContext.Provider
      value={{
        sessions,
        loading,
        error,
        listSessionsByAgreement,
        requestSessionStart,
        approveSessionStart,
        completeSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}
